import { isEmpty } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';

import { ordersApi, tradeMethodsApi } from 'api';
import { Loader, QrCode } from 'components';
import { OrderContext } from 'context';
import { DeviceOS, QueryKey } from 'enums';
import { useDeepLink } from 'hooks';
import { useOrderAuth } from 'pages/Order/hooks';
import { Order, TradeMethod } from 'types';
import { userDeviceUtils } from 'utils';

import { getMatchingDeeplinks } from './deeplink-page.utils';

const TEXT = {
  unavailable: 'Ссылка недоступна',
  noDeeplinks: 'Нет доступных диплинков',
  openingTitle: 'Открываем приложение банка',
  openingDescription:
    'Если приложение не открылось автоматически, нажмите кнопку ниже.',
  openButton: 'Открыть приложение банка',
  backButton: 'Вернуться к заказу',
  fallbackDescription: 'Вернитесь на страницу заказа или попробуйте позже.',
};

export const DeeplinkPage: React.FC = () => {
  const {
    orderId,
    authenticated,
    authenticationFailed,
    setAuthenticationFailed,
    isAuthLoading,
  } = useOrderAuth();

  const orderQuery = useQuery(
    [QueryKey.Order, orderId, 'deeplink-page'],
    () => ordersApi.getOne(orderId),
    {
      enabled: authenticated,
      onError: () => setAuthenticationFailed(true),
    },
  );

  const tradeMethodQuery = useQuery(
    [QueryKey.TradeMethods, orderId, 'selected-deeplink-page'],
    () => tradeMethodsApi.getSelectedTradeMethod(orderId),
    {
      enabled: authenticated,
      onError: () => setAuthenticationFailed(true),
    },
  );

  if (isAuthLoading || orderQuery.isLoading || tradeMethodQuery.isLoading) {
    return <Loader />;
  }

  if (
    authenticationFailed ||
    orderQuery.isError ||
    tradeMethodQuery.isError ||
    !orderQuery.data
  ) {
    return <DeeplinkPageMessage title={TEXT.unavailable} />;
  }

  return (
    <OrderContext.Provider value={{ order: orderQuery.data } as any}>
      <DeeplinkLauncher
        order={orderQuery.data}
        tradeMethod={tradeMethodQuery.data}
      />
    </OrderContext.Provider>
  );
};

export const DeeplinkLauncher: React.FC<{
  order: Order;
  tradeMethod: TradeMethod | undefined;
}> = ({ order, tradeMethod }) => {
  const { processDeeplinkUrl, openDeepLinks } = useDeepLink();
  const [searchParams] = useSearchParams();
  const autoOpenAttemptedRef = useRef(false);
  const deviceOS = useMemo(() => userDeviceUtils.getDeviceOS(), []);
  const customerBank =
    searchParams.get('customerBank') || order.payment?.customerBank;

  const processedUrls = useMemo(
    () =>
      getMatchingDeeplinks({
        deeplinks: tradeMethod?.deeplinks,
        customerBank,
        deviceOS,
      }).map((deeplink) => processDeeplinkUrl(deeplink.url)),
    [customerBank, deviceOS, processDeeplinkUrl, tradeMethod?.deeplinks],
  );

  const deeplinkUrl = useMemo(() => processedUrls.join(','), [processedUrls]);

  const open = useCallback(async () => {
    if (!deeplinkUrl) {
      return;
    }
    await openDeepLinks(deeplinkUrl);
  }, [deeplinkUrl, openDeepLinks]);

  useEffect(() => {
    if (!deeplinkUrl || autoOpenAttemptedRef.current) {
      return;
    }
    autoOpenAttemptedRef.current = true;
    void open();
  }, [deeplinkUrl, open]);

  if (isEmpty(processedUrls)) {
    return <DeeplinkPageMessage title={TEXT.noDeeplinks} />;
  }

  return (
    <div className="tw-min-h-[70vh] tw-flex tw-items-center tw-justify-center tw-p-4">
      <div className="tw-w-full tw-max-w-md tw-text-center">
        <h1 className="tw-text-xl tw-font-semibold tw-mb-3">
          {TEXT.openingTitle}
        </h1>
        <p className="tw-text-gray-600 tw-mb-6">{TEXT.openingDescription}</p>
        <button
          type="button"
          onClick={open}
          className="tw-w-full tw-h-[52px] tw-rounded-full tw-bg-blue-600 tw-text-white tw-font-medium"
        >
          {TEXT.openButton}
        </button>
        <BackToOrderButton />
        {deviceOS === DeviceOS.Desktop && (
          <div className="tw-mt-6">
            <QrCode data={processedUrls[0]} />
          </div>
        )}
      </div>
    </div>
  );
};

const DeeplinkPageMessage: React.FC<{
  title: string;
}> = ({ title }) => (
  <div className="tw-min-h-[70vh] tw-flex tw-items-center tw-justify-center tw-p-4">
    <div className="tw-w-full tw-max-w-md tw-text-center">
      <h1 className="tw-text-xl tw-font-semibold tw-mb-3">{title}</h1>
      <p className="tw-text-gray-600">{TEXT.fallbackDescription}</p>
      <div className="tw-mt-6">
        <BackToOrderButton />
      </div>
    </div>
  </div>
);

const BackToOrderButton: React.FC = () => {
  const canGoBack = typeof window !== 'undefined' && window.history.length > 1;
  const goBack = useCallback(() => {
    window.history.back();
  }, []);

  if (!canGoBack) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={goBack}
      className="tw-mt-3 tw-inline-flex tw-w-full tw-h-[52px] tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-gray-300 tw-bg-white tw-text-gray-800 tw-font-medium"
    >
      {TEXT.backButton}
    </button>
  );
};
